import { type ApiClient, ApiError } from '@classgo/app-kit';
import type { GradingScaleInput } from '@classgo/shared';
import { queryOptions } from '@tanstack/react-query';

import type { Entity, GradingScaleView, Page, ViewOf } from './types';

const PATHS: Record<Entity, string> = {
  sedes: '/school/sedes',
  years: '/school/years',
  periods: '/school/periods',
  levels: '/school/levels',
  grades: '/school/grades',
  groups: '/school/groups',
  subjects: '/school/subjects',
  classrooms: '/school/classrooms',
};

const withQuery = (path: string, query: Record<string, string | undefined> = {}) => {
  const params = new URLSearchParams({ pageSize: '200' });
  for (const [key, value] of Object.entries(query)) {
    if (value) params.set(key, value);
  }
  return `${path}?${params.toString()}`;
};

/**
 * The academic endpoints (`/school/*`), one shape for every entity plus the year and period
 * actions and the grading scale.
 *
 * @param {ApiClient} api -- The client.
 * @returns The calls.
 */
export const academicApi = (api: ApiClient) => ({
  list: <E extends Entity>(entity: E, query?: Record<string, string | undefined>) =>
    api.request<Page<ViewOf[E]>>(withQuery(PATHS[entity], query)),
  create: <E extends Entity>(entity: E, body: unknown) =>
    api.request<ViewOf[E]>(PATHS[entity], { method: 'POST', body }),
  update: <E extends Entity>(entity: E, id: string, body: unknown) =>
    api.request<ViewOf[E]>(`${PATHS[entity]}/${id}`, { method: 'PATCH', body }),
  remove: (entity: Entity, id: string) =>
    api.request<unknown>(`${PATHS[entity]}/${id}`, { method: 'DELETE' }),
  activateYear: (id: string) =>
    api.request<ViewOf['years']>(`${PATHS.years}/${id}/activate`, { method: 'POST' }),
  openPeriod: (id: string) =>
    api.request<ViewOf['periods']>(`${PATHS.periods}/${id}/open`, { method: 'POST' }),
  closePeriod: (id: string) =>
    api.request<ViewOf['periods']>(`${PATHS.periods}/${id}/close`, { method: 'POST' }),
  gradingScale: async (yearId: string): Promise<GradingScaleView | null> => {
    try {
      return await api.request<GradingScaleView>(`${PATHS.years}/${yearId}/grading-scale`);
    } catch (error) {
      if (error instanceof ApiError && error.code === 'NOT_FOUND') return null;
      throw error;
    }
  },
  putGradingScale: (yearId: string, body: GradingScaleInput) =>
    api.request<GradingScaleView>(`${PATHS.years}/${yearId}/grading-scale`, {
      method: 'PUT',
      body,
    }),
});

/**
 * Query options of a list; the key carries the filters so each combination caches on its own.
 *
 * @param {ApiClient} api -- The client.
 * @param {Entity} entity -- The entity.
 * @param {Record<string, string | undefined>} query -- Filters (`sedeId`, `schoolYearId`, `levelId`).
 * @returns The options.
 */
export const listQuery = <E extends Entity>(
  api: ApiClient,
  entity: E,
  query: Record<string, string | undefined> = {},
) =>
  queryOptions({
    queryKey: ['school', entity, query],
    queryFn: () => academicApi(api).list(entity, query),
  });

/**
 * Query options of a year's grading scale (`null` until configured).
 *
 * @param {ApiClient} api -- The client.
 * @param {string} yearId -- The school year.
 * @returns The options.
 */
export const gradingScaleQuery = (api: ApiClient, yearId: string) =>
  queryOptions({
    queryKey: ['school', 'grading-scale', yearId],
    queryFn: () => academicApi(api).gradingScale(yearId),
  });
